/**
 * LedgerExplorerPage — search + drill into the Unified Finance Ledger.
 *
 * Reads exclusively from /api/finance/ledger/* — the single source of truth.
 * Every row shown has full 6-dimension scope (tenant / event / branch /
 * module / etc) and an immutable audit trail accessible via drill-down.
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  BookOpenCheck, Search, Filter, RefreshCw, Loader2,
  ArrowUpCircle, ArrowDownCircle, Clock, ShieldCheck, X,
} from 'lucide-react';
import api from '../lib/api';
import { formatCurrency as fmtCurrency } from '../lib/currency';

const fmt = (v, cur = 'USD') => fmtCurrency(v, cur || 'USD');

const MODULES = ['', 'event_finance', 'bookshop', 'donations', 'stripe', 'payment_plan', 'pos', 'school'];
const WORKSPACES = ['', 'events', 'school', 'church', 'bookshop', 'pos', 'donations', 'housing', 'studio'];
const DIRECTIONS = ['', 'income', 'expense'];
const STATUSES = ['', 'pending', 'approved', 'posted', 'void', 'refunded'];

export default function LedgerExplorerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [module, setModule] = useState('');
  const [workspace, setWorkspace] = useState('');
  const [direction, setDirection] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [days, setDays] = useState(180);
  const [drill, setDrill] = useState(null);

  // Hydrate filters from URL query params on first mount so /admin/finance/ledger?workspace=church
  // (from the Executive Dashboard sparklines) lands the user on a pre-filtered view.
  useEffect(() => {
    const ws = searchParams.get('workspace');
    const md = searchParams.get('module');
    const dr = searchParams.get('direction');
    const st = searchParams.get('status');
    const dy = searchParams.get('days');
    const qs = searchParams.get('q');
    if (ws) setWorkspace(ws);
    if (md) setModule(md);
    if (dr) setDirection(dr);
    if (st) setStatus(st);
    if (dy) setDays(parseInt(dy, 10) || 180);
    if (qs) setQ(qs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: caps } = useQuery({
    queryKey: ['finance-me-caps'],
    queryFn: () => api.get('/finance/me/capabilities').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });
  const canRead = (caps?.underlying_capabilities || caps?.permissions || []).includes('read_reports');

  const params = { module: module || undefined, workspace: workspace || undefined, direction: direction || undefined, status: status || undefined,
                   q: q || undefined, days, limit: 100 };
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['ledger-transactions', params],
    queryFn: () => api.get('/finance/ledger/transactions', { params }).then(r => r.data),
    enabled: !!caps && canRead,
  });

  const { data: audit } = useQuery({
    queryKey: ['ledger-audit', drill?.id],
    queryFn: () => api.get(`/finance/ledger/audit/${drill.id}`).then(r => r.data),
    enabled: !!drill?.id,
  });

  const { data: verify } = useQuery({
    queryKey: ['ledger-verify'],
    queryFn: () => api.get('/finance/ledger/verify').then(r => r.data),
    enabled: !!caps && canRead,
    staleTime: 60000,
  });

  if (!caps) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;

  if (!canRead) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
        <Card className="max-w-md">
          <CardContent className="p-6">
            <p className="font-semibold text-slate-900">Ledger access required</p>
            <p className="text-sm text-slate-600 mt-2">Your finance roles: <span className="font-mono">{(caps.roles || []).join(', ') || 'none'}</span>. Ask a super-admin for <code>finance.view.ledger</code> (auditor, treasurer, or finance_admin).</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="ledger-explorer-page">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header + verify chip */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BookOpenCheck className="h-6 w-6 text-emerald-600" />
              Ledger Explorer
            </h1>
            <p className="text-sm text-slate-500">
              Every transaction in the ecosystem — single source of truth (<code>finance_transactions</code>).
            </p>
          </div>
          {verify && (
            <div className="flex items-center gap-2 text-xs">
              <Badge variant="outline" className="bg-white" data-testid="ledger-verify-total">
                {verify.unified.total_transactions} total · {verify.unified.posted} posted
              </Badge>
              <Badge variant="outline" className="bg-white">
                event_finance {verify.unified.by_module.event_finance} · bookshop {verify.unified.by_module.bookshop} · donations {verify.unified.by_module.donations}
              </Badge>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4 flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-slate-400" />
            <select value={workspace} onChange={e => setWorkspace(e.target.value)} className="text-sm border rounded h-9 px-2 bg-white" data-testid="ledger-workspace">
              {WORKSPACES.map(w => <option key={w} value={w}>{w || 'All workspaces'}</option>)}
            </select>
            <select value={module} onChange={e => setModule(e.target.value)} className="text-sm border rounded h-9 px-2 bg-white" data-testid="ledger-module">
              {MODULES.map(m => <option key={m} value={m}>{m || 'All modules'}</option>)}
            </select>
            <select value={direction} onChange={e => setDirection(e.target.value)} className="text-sm border rounded h-9 px-2 bg-white" data-testid="ledger-direction">
              {DIRECTIONS.map(m => <option key={m} value={m}>{m || 'All directions'}</option>)}
            </select>
            <select value={status} onChange={e => setStatus(e.target.value)} className="text-sm border rounded h-9 px-2 bg-white" data-testid="ledger-status">
              {STATUSES.map(m => <option key={m} value={m}>{m || 'All statuses'}</option>)}
            </select>
            <select value={days} onChange={e => setDays(parseInt(e.target.value, 10))} className="text-sm border rounded h-9 px-2 bg-white" data-testid="ledger-days">
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>1 year</option>
              <option value={1095}>3 years</option>
            </select>
            <div className="flex-1 min-w-0 sm:min-w-[200px] flex items-center gap-1">
              <Search className="h-4 w-4 text-slate-400" />
              <Input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search description / payer / vendor…"
                className="h-9"
                data-testid="ledger-search"
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="ledger-refresh">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
          </CardContent>
        </Card>

        {/* Transactions table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">
              {isLoading ? 'Loading…' : `${data?.total || 0} transactions · showing ${data?.rows?.length || 0}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>
            ) : (data?.rows || []).length === 0 ? (
              <p className="p-8 text-center text-slate-400">No transactions match these filters.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 uppercase tracking-wide border-b">
                  <tr>
                    <th className="text-left py-2 px-3">When</th>
                    <th className="text-left py-2 px-3">Module</th>
                    <th className="text-left py-2 px-3">Description</th>
                    <th className="text-left py-2 px-3">Counterparty</th>
                    <th className="text-left py-2 px-3">Category</th>
                    <th className="text-right py-2 px-3">Amount</th>
                    <th className="text-right py-2 px-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.rows || []).map(row => (
                    <tr
                      key={row.id}
                      className="border-b last:border-0 hover:bg-slate-50 cursor-pointer"
                      onClick={() => setDrill(row)}
                      data-testid={`ledger-row-${row.id}`}
                    >
                      <td className="py-2 px-3 text-xs text-slate-500 whitespace-nowrap">
                        {new Date(row.occurred_at).toLocaleDateString()}
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className="text-xs">{row.source.module}</Badge>
                      </td>
                      <td className="py-2 px-3 truncate max-w-[240px]">{row.description || '—'}</td>
                      <td className="py-2 px-3 text-slate-500 truncate max-w-[160px]">
                        {row.counterparty?.name || '—'}
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-500 capitalize">
                        {row.category?.replace(/_/g, ' ')}
                      </td>
                      <td className={`py-2 px-3 text-right font-semibold whitespace-nowrap ${row.direction === 'income' ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {row.direction === 'income' ? '+' : '-'}{fmt(row.amount, row.currency)}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <StatusBadge status={row.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Drill-down drawer */}
        <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {drill?.direction === 'income'
                  ? <ArrowUpCircle className="h-5 w-5 text-emerald-600" />
                  : <ArrowDownCircle className="h-5 w-5 text-rose-600" />}
                Transaction · {drill?.id}
              </DialogTitle>
            </DialogHeader>
            {drill && (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Amount" value={<span className={drill.direction === 'income' ? 'text-emerald-700 font-bold' : 'text-rose-700 font-bold'}>{fmt(drill.amount, drill.currency)}</span>} />
                  <Field label="Status" value={<StatusBadge status={drill.status} />} />
                  <Field label="Occurred" value={new Date(drill.occurred_at).toLocaleString()} />
                  <Field label="Module" value={<Badge variant="outline">{drill.source.module}</Badge>} />
                  <Field label="Category" value={drill.category} />
                  <Field label="Direction" value={drill.direction} />
                </div>
                <Field label="Description" value={drill.description || '—'} />
                <Field label="Counterparty" value={drill.counterparty?.name ? `${drill.counterparty.name} (${drill.counterparty.type || 'party'})` : '—'} />
                <Field label="Scope" value={
                  <div className="flex flex-wrap gap-1 text-xs">
                    {Object.entries(drill.scope || {}).filter(([, v]) => v).map(([k, v]) => (
                      <Badge key={k} variant="outline" className="font-mono">{k}: {String(v).slice(-8)}</Badge>
                    ))}
                  </div>
                } />
                <Field label="Source ID" value={<code className="text-xs">{drill.source?.external_id}</code>} />

                <div className="pt-3 border-t">
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" /> Audit trail
                  </p>
                  {audit?.trail?.length ? (
                    <ul className="space-y-2 max-h-64 overflow-y-auto">
                      {audit.trail.map(a => (
                        <li key={a.id} className="border rounded p-2 text-xs bg-slate-50">
                          <div className="flex items-center gap-2 mb-1">
                            <Clock className="h-3 w-3 text-slate-400" />
                            <span className="text-slate-500">{new Date(a.timestamp).toLocaleString()}</span>
                            <Badge variant="outline" className="text-xs">{a.action}</Badge>
                          </div>
                          <div className="text-slate-600">by {a.actor_email || a.actor_id || 'system'}</div>
                          {a.new?.status && <div className="mt-1 text-slate-500">→ status: <code>{a.new.status}</code></div>}
                          {a.new?.reason && <div className="text-slate-500 italic">&ldquo;{a.new.reason}&rdquo;</div>}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-slate-400">No audit entries yet.</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = {
    posted:   'bg-emerald-100 text-emerald-700 border-emerald-200',
    approved: 'bg-blue-100 text-blue-700 border-blue-200',
    pending:  'bg-amber-100 text-amber-700 border-amber-200',
    void:     'bg-slate-100 text-slate-500 border-slate-200',
    refunded: 'bg-rose-100 text-rose-700 border-rose-200',
  }[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  return <Badge variant="outline" className={`text-[10px] ${cls}`}>{status}</Badge>;
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      <div className="text-slate-700">{value}</div>
    </div>
  );
}
